// Historia 179 — la opción "Without workflow" (RN9).
//
// La opción la añade JUPITER, Sun no la devuelve (C10 sin cambios). Se reconoce por su IDENTIDAD, nunca
// por su texto.
//
// ⚠️ El id reservado es una CADENA a propósito: los ids de workflow de Sun son números, así que no hay
// valor de Sun que pueda coincidir con él.
export const WITHOUT_WORKFLOW_OPTION_ID = 'h179-without-workflow'

export const isWithoutWorkflowOption = (workflow) => !!workflow && workflow.id === WITHOUT_WORKFLOW_OPTION_ID

// Una fila del listado (C4) sin workflow llega con `workflow: null`: el indicador se deriva de ahí, no de
// un campo nuevo (C4 no añade ninguno).
export const rowHasWorkflow = (row) => !!(row && row.workflow)

// El mismo orden que el selector aplicaba por su cuenta (`SmartSelect`, `sortByLabel`): alfabético y sin
// distinguir mayúsculas. Se ordena aquí porque la centinela tiene que ir PRIMERO, y el selector, si
// ordenara él, la mezclaría con los demás.
const byLabel = (a, b) => String(a.name ?? '').toLowerCase().localeCompare(String(b.name ?? '').toLowerCase())

/**
 * La etiqueta de la centinela, distinta de todos los nombres reales de la lista (R11).
 *
 * ⚠️ No es cosmético: el selector del alta (`SmartSelect`) trabaja con CADENAS y la tabla recupera el
 * objeto buscándolo POR NOMBRE (V20f). Con dos opciones escritas igual, la tabla se quedaría con la
 * primera y elegir el workflow real enviaría "sin workflow" —o al revés—. Así que en colisión la
 * centinela se envuelve entre paréntesis (como se lee una opción que no es un registro), las veces que
 * haga falta, y sigue diciendo lo que es.
 */
const distinctLabel = (label, workflows) => {
  const names = new Set(workflows.map((workflow) => workflow.name))
  let candidate = label
  while (names.has(candidate)) candidate = `(${candidate})`
  return candidate
}

/** Los workflows de C10 (`[{ id, name }]`) en el orden del selector; sin lista, ninguno. */
export const sortedWorkflowOptions = (workflows) => (Array.isArray(workflows) ? [...workflows].sort(byLabel) : [])

/**
 * Opciones del selector: "Without workflow" primero y después los de C10 por orden alfabético.
 * `workflows` es lo que sirve C10 (`[{ id, name }]`); si todavía no hay lista, solo la centinela.
 */
export const workflowOptionsWithSentinel = (workflows, t_) => {
  const list = sortedWorkflowOptions(workflows)
  return [{ id: WITHOUT_WORKFLOW_OPTION_ID, name: distinctLabel(t_('Without workflow'), list) }, ...list]
}

// ¿El alta no eligió ningún workflow? El selector deja la fila sin `workflow` o con uno sin `id`.
const hasNoWorkflowChosen = (row) => !row.workflow || row.workflow.id === undefined || row.workflow.id === null || row.workflow.id === ''

/**
 * El cuerpo del alta (C3). Con la centinela: `workflow: null` + `withoutWorkflow: true`, y el id
 * reservado no sale de Jupiter. Sin elegir nada: `workflow: null` + `withoutWorkflowIfNoDefault: true`,
 * así Sun asigna el workflow por defecto de la Part Reference y, si no tiene, la crea sin workflow.
 * Con un workflow real: la fila tal cual, sin ninguna de las dos claves.
 */
export const creationBodyOf = (row) => {
  if (isWithoutWorkflowOption(row.workflow)) return { ...row, workflow: null, withoutWorkflow: true }
  if (hasNoWorkflowChosen(row)) return { ...row, workflow: null, withoutWorkflowIfNoDefault: true }
  return row
}

/**
 * El cuerpo de "Copy as" (RN15). La copia de una OF sin workflow es otra OF sin workflow —nunca se le
 * inventa uno—, y sin `withoutWorkflow` C3 intentaría el workflow por defecto (G19). Con workflow, la
 * copia sigue EXACTAMENTE como hoy: reenvía el de la fila (V4), sin la clave nueva.
 */
export const copyBodyOf = (row) => (rowHasWorkflow(row) ? row : { ...row, workflow: null, withoutWorkflow: true })

/**
 * ¿El cambio de estado masivo a `target` queda bloqueado por una OF sin workflow de la selección? Una OF sin
 * workflow nunca se lanza (LAUNCHED) y, cerrada, no vuelve a PENDING (Sun lo rechaza igual; aquí solo se
 * deshabilita la opción). La selección de la tabla solo trae ids, así que el workflow y el estado de cada
 * fila salen de los índices construidos al cargar. Con «seleccionar todo» se miran todas las filas cargadas.
 *
 * @param {string} target Estado destino.
 * @param {{ allSelected: boolean, rows: Array<{ id: (string|number), status: string }> }} selection
 * @param {Object<string, boolean>} withoutWorkflowById id → la OF no tiene workflow.
 * @param {Object<string, string>} statusById id → estado actual.
 */
export const isBulkStatusBlockedByWithoutWorkflow = (target, selection, withoutWorkflowById, statusById) => {
  const ids = selection.allSelected
    ? Object.keys(withoutWorkflowById)
    : selection.rows.map((row) => String(row.id))
  const withoutWorkflowIds = ids.filter((id) => withoutWorkflowById[id])
  if (target === 'LAUNCHED') return withoutWorkflowIds.length > 0
  if (target === 'PENDING') return withoutWorkflowIds.some((id) => statusById[id] === 'CLOSED')
  return false
}
